<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg flex flex-col shadow-lg border border-gray-700 animate-scaleIn overflow-hidden">

      <!-- ── Header ── -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-700">
        <h3 class="text-sm font-bold text-gray-100 flex items-center gap-2">
          <i class="fas fa-sack-dollar text-red-400"></i>
          ดักรอปล้น
        </h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-200 transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- ── Body ── -->
      <div class="overflow-y-auto scrollbar-thin loot-body-scroll p-4">

        <!-- Loading -->
        <div v-if="isLoading" class="flex flex-col items-center py-10 gap-3">
          <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-red-500"></div>
          <p class="text-sm text-gray-400">กำลังโหลด...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="flex flex-col gap-3">
          <div class="flex items-center gap-2 bg-red-900-20 border border-red-800 rounded-lg px-3 py-3 text-sm text-red-300">
            <i class="fas fa-exclamation-triangle flex-shrink-0"></i>
            {{ error }}
          </div>
          <button @click="$emit('close')" class="w-full py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg border border-gray-600 transition-colors">
            ปิด
          </button>
        </div>

        <!-- Content -->
        <div v-else class="flex flex-col gap-4">

          <!-- Item info -->
          <div class="flex items-center gap-3 bg-gray-900 rounded-lg p-3 border border-gray-700">
            <img
              :src="item?.img || defaultItemImg"
              class="w-12 h-12 rounded-lg object-cover border border-gray-600 flex-shrink-0"
              @error="handleImageError"
            />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-gray-100 truncate">{{ item?.name || 'Unknown' }}</p>
              <div class="flex flex-wrap gap-1.5 mt-1">
                <span class="text-xxs bg-gray-700 border border-gray-600 px-1.5 py-0.5 rounded text-gray-300">
                  <i class="fas fa-clock text-gray-400 mr-1"></i>{{ item?.waitTimeMinutes || 0 }} นาที
                </span>
                <span v-if="item?.minLootItems" class="text-xxs bg-gray-700 border border-gray-600 px-1.5 py-0.5 rounded text-gray-300">
                  <i class="fas fa-box text-gray-400 mr-1"></i>{{ item.minLootItems }}–{{ item.maxLootItems }} ชิ้น
                </span>
                <span v-if="item?.silverReward" class="text-xxs bg-gray-700 border border-gray-600 px-1.5 py-0.5 rounded text-gray-300">
                  <i class="fas fa-coins text-gray-100 mr-1"></i> {{ item.silverReward }}
                </span>
                <span v-if="item?.goldReward" class="text-xxs bg-gray-700 border border-gray-600 px-1.5 py-0.5 rounded text-gray-300">
                 <i class="fas fa-g text-yellow-400 mr-1"></i> {{ item.goldReward }}
                </span>
                <span v-if="item?.diamondReward" class="text-xxs bg-gray-700 border border-gray-600 px-1.5 py-0.5 rounded text-gray-300">
                  <i class="fas fa-gem text-blue-400 mr-1"></i> {{ item.diamondReward }}
                </span>
              </div>
            </div>
          </div>

          <!-- Warning -->
          <div class="flex items-center gap-2 text-xs text-yellow-400 bg-yellow-900-20 border border-yellow-800-50 rounded-lg px-3 py-2">
            <i class="fas fa-exclamation-triangle flex-shrink-0"></i>
            การใช้ไอเทมจะถูก<strong>ลบทันที</strong> ไม่สามารถย้อนกลับได้
          </div>

          <!-- Map selection label -->
          <div>
            <label class="block text-xs font-semibold text-gray-300 mb-2">
              <i class="fas fa-map text-indigo-400 mr-1"></i>
              เลือกแผนที่ดักรอ <span class="text-red-400">*</span>
            </label>

            <div v-if="allowedMaps.length === 0" class="text-xs text-yellow-400 bg-yellow-900-20 border border-yellow-800-50 rounded-lg px-3 py-3 flex items-center gap-2">
              <i class="fas fa-ban"></i> ไม่มีแผนที่ที่อนุญาตสำหรับไอเทมนี้
            </div>

            <!-- Map grid -->
            <div v-else class="grid grid-cols-1 gap-2 max-h-52 overflow-y-auto scrollbar-thin pr-1">
              <div
                v-for="map in allowedMaps"
                :key="map.id"
                @click="selectedMap = map.id"
                class="flex items-center gap-2 bg-gray-900 rounded-lg p-2 border cursor-pointer transition-all"
                :class="selectedMap === map.id
                  ? 'border-indigo-500 map-selected-bg'
                  : 'border-gray-700 hover:border-gray-500'"
              >
                <img
                  :src="map.imageUrl || 'default-map.png'"
                  class="w-10 h-10 rounded object-cover flex-shrink-0 border border-gray-700"
                  @error="handleImageError"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-semibold text-gray-200 truncate">{{ map.name }}</p>
                  <p class="text-xxs text-gray-500">Lv.{{ map.requiredLevel }}</p>
                </div>
                <i
                  v-if="selectedMap === map.id"
                  class="fas fa-check-circle text-indigo-400 text-sm flex-shrink-0"
                ></i>
              </div>
            </div>
          </div>

          <!-- Scout animation while looting -->
          <div v-if="isLooting" class="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
            <div class="relative h-14">
              <div class="absolute scout-path-line left-2 right-2 border-t-2 border-dashed border-gray-700"></div>
              <div class="scout-runner absolute left-2" :class="{ 'scout-go': moveToRight }">
                <span class="text-2xl">👤</span>
              </div>
              <span class="bush absolute bottom-1 left-3 text-base scout-bush animate-wiggle">🌿</span>
              <span class="bush absolute bottom-1 scout-bush-center text-base scout-bush animate-wiggle-slow">🌾</span>
              <span class="bush absolute bottom-1 right-3 text-base scout-bush animate-wiggle">🔪</span>
            </div>
            <p class="text-center text-xxs text-gray-500 italic pb-2">{{ hintText }}</p>
          </div>

          <!-- Action buttons -->
          <div class="flex flex-col gap-2">
            <button
              @click="startLoot"
              :disabled="isLooting || !selectedMap || allowedMaps.length === 0"
              class="w-full flex items-center justify-center gap-2 py-2.5 bg-red-700 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg border border-red-600 transition-colors"
            >
              <div v-if="isLooting" class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
              <i v-else class="fas fa-sack-dollar"></i>
              {{ isLooting ? 'กำลังวางตำแหน่ง...' : 'เริ่มดักรอปล้น' }}
            </button>
            <button
              @click="$emit('close')"
              class="w-full py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-semibold rounded-lg border border-gray-600 transition-colors"
            >
              ยกเลิก
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import defaultItemImg from '@/assets/default-item.png';

export default {
  name: 'LootModal',
  props: {
    item: { type: Object, required: true },
    mapName: { type: String, default: '' }
  },
  emits: ['close', 'loot-success', 'start-battle'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const isLoading = ref(true);
    const error = ref(null);
    const isAdmin = computed(() => !!playerStore.userData?.admin);
    const isLooting = ref(false);
    const hintText = ref('กำลังหาตำแหน่งซุ่ม...');
    const moveToRight = ref(false);
    const allowedMaps = ref([]);
    const selectedMap = ref('');

    // FIX #9 – store reference so we can unsubscribe later
    let unsubLootEvents = null;

    const lootMap = [
      { name: 'หมวกไอ้โม่ง', map: ['รถไฟฟ้าใต้ดิน','โรงพยาบาล','หมู่บ้านคนรวย','สวนสนุก','โรงงานอุตสาหกรรม','ห้างสรรพสินค้า','สถานีดับเพลิงร้าง','ค่ายทหาร','สถาบันวิจัยหุ่นยนต์','สถาบันวิจัยไวรัส','สุสานนักรบโบราณ','ตึกความมันคงแห่งชาติ','โรงเก็บศพจักรกล','ตึกปฏิบัติการพันธุกรรม','เหมืองแร่ร้าง','หอสมุดแห่งชาติ','ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์','สถานกักกันสิ่งมีชีวิตกลายพันธุ์','สวนสัตว์จักรกล'] },
      { name: 'หมวกไอ้โม่งลายทหาร', map: ['สถานีดับเพลิงร้าง','ค่ายทหาร','สถาบันวิจัยหุ่นยนต์','สถาบันวิจัยไวรัส','สุสานนักรบโบราณ','ตึกความมันคงแห่งชาติ','โรงเก็บศพจักรกล','ตึกปฏิบัติการพันธุกรรม','เหมืองแร่ร้าง','หอสมุดแห่งชาติ','ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์','สถานกักกันสิ่งมีชีวิตกลายพันธุ์','สวนสัตว์จักรกล'] },
      { name: 'หมวกไอ้โม่งหุ่นยนต์', map: ['สถาบันวิจัยหุ่นยนต์','สถาบันวิจัยไวรัส','สุสานนักรบโบราณ','ตึกความมันคงแห่งชาติ','โรงเก็บศพจักรกล','ตึกปฏิบัติการพันธุกรรม','เหมืองแร่ร้าง','หอสมุดแห่งชาติ','ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์','สถานกักกันสิ่งมีชีวิตกลายพันธุ์','สวนสัตว์จักรกล'] },

      { name: 'หมวกไอ้โม่งผี', map: ['สนามกอล์ฟวิญญาณ','โรงเรียนวิญญาณ','มหาวิทยาลัยวิญญาณ','รถไฟฟ้าใต้ดินวิญญาณ','โรงพยาบาลวิญญาณ','หมู่บ้านคนรวยวิญญาณ','วัดวิญญาณ','สวนสนุกวิญญาณ','โรงงานอุตสาหกรรมวิญญาณ','ห้างสรรพสินค้าวิญญาณ','สถานีดับเพลิงวิญญาณ','ค่ายทหารวิญญาณ','พระบรมมหาราชวังวิญญาณ','ราชมังคลากีฬาสถานวิญญาณ'] },
      { name: 'หมวกไอ้โม่งตัวตลก', map: ['วัดวิญญาณ','สวนสนุกวิญญาณ','โรงงานอุตสาหกรรมวิญญาณ','ห้างสรรพสินค้าวิญญาณ','สถานีดับเพลิงวิญญาณ','ค่ายทหารวิญญาณ','พระบรมมหาราชวังวิญญาณ','ราชมังคลากีฬาสถานวิญญาณ'] },
      { name: 'หมวกไอ้โม่งผีกระโหลก', map: ['วัดวิญญาณ','สวนสนุกวิญญาณ','โรงงานอุตสาหกรรมวิญญาณ','ห้างสรรพสินค้าวิญญาณ','สถานีดับเพลิงวิญญาณ','ค่ายทหารวิญญาณ','พระบรมมหาราชวังวิญญาณ','ราชมังคลากีฬาสถานวิญญาณ'] },
      { name: 'หมวกไอ้โม่งเพชฌฆาต', map: ['วัดวิญญาณ','สวนสนุกวิญญาณ','โรงงานอุตสาหกรรมวิญญาณ','ห้างสรรพสินค้าวิญญาณ','สถานีดับเพลิงวิญญาณ','ค่ายทหารวิญญาณ','พระบรมมหาราชวังวิญญาณ','ราชมังคลากีฬาสถานวิญญาณ'] },
      { name: 'หมวกไอ้โม่งมหายักษ์ผู้พิพากษา', map: ['วัดวิญญาณ','สวนสนุกวิญญาณ','โรงงานอุตสาหกรรมวิญญาณ','ห้างสรรพสินค้าวิญญาณ','สถานีดับเพลิงวิญญาณ','ค่ายทหารวิญญาณ','พระบรมมหาราชวังวิญญาณ','ราชมังคลากีฬาสถานวิญญาณ'] },

      { name: 'หมวกไอโม่งช่างเชื่อม', map: ['โรงเก็บศพจักรกล','ตึกปฏิบัติการพันธุกรรม','เหมืองแร่ร้าง','หอสมุดแห่งชาติ','ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์','สถานกักกันสิ่งมีชีวิตกลายพันธุ์','สวนสัตว์จักรกล'] },
      { name: 'หมวกไอ้โม่งครุฑ', map: ['โรงเก็บศพจักรกล','ตึกปฏิบัติการพันธุกรรม','เหมืองแร่ร้าง','หอสมุดแห่งชาติ','ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์','สถานกักกันสิ่งมีชีวิตกลายพันธุ์','สวนสัตว์จักรกล'] },
      { name: 'หมวกไอ้โม่งราชานักปล้น', map: ['ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์','สถานกักกันสิ่งมีชีวิตกลายพันธุ์','สวนสัตว์จักรกล'] },
    ];

    // Custom order matching ExploreMapModal
    const customMapOrder = [
      'ตรอกซอย','ตลาด','สนามกอล์ฟ','โรงเรียน','มหาวิทยาลัย','โกดัง','รถไฟฟ้าใต้ดิน',
      'โรงพยาบาล','สยามสแควร์','หมู่บ้านคนรวย','สวนสนุก','โรงงานอุตสาหกรรม','ห้างสรรพสินค้า',
      'สถานีดับเพลิงร้าง','ค่ายทหาร','ราชมังคลากีฬาสถาน',
      'สถาบันวิจัยหุ่นยนต์','สถาบันวิจัยไวรัส','พระบรมมหาราชวัง','ป่าช้า','สุสานนักรบโบราณ','ตึกความมันคงแห่งชาติ',
      'โรงเก็บศพจักรกล','ตึกปฏิบัติการพันธุกรรม','เหมืองแร่ร้าง','หอสมุดแห่งชาติ','ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์','สถานกักกันสิ่งมีชีวิตกลายพันธุ์',
      // supernatural
      'ตลาดวิญญาณ','สนามกอล์ฟวิญญาณ','โรงเรียนวิญญาณ','มหาวิทยาลัยวิญญาณ','รถไฟฟ้าใต้ดินวิญญาณ',
      'โรงพยาบาลวิญญาณ','หมู่บ้านคนรวยวิญญาณ','วัดวิญญาณ','สวนสนุกวิญญาณ',
      'โรงงานอุตสาหกรรมวิญญาณ','ห้างสรรพสินค้าวิญญาณ','สถานีดับเพลิงวิญญาณ','ค่ายทหารวิญญาณ','พระบรมมหาราชวังวิญญาณ',
    ];

    const handleImageError = e => { e.target.src = defaultItemImg; };

    const fetchMaps = async () => {
      try {
        let q = projectFirestore.collection('maps').where('isLootingAllowed', '==', true);
        if (!isAdmin.value) q = q.where('isActive', '==', true);
        const snap = await q.get();
        const itemMaps = lootMap.find(l => l.name === props.item.name)?.map || [];
        allowedMaps.value = snap.docs
          .map(d => ({ id: d.id, name: d.data().name, imageUrl: d.data().imageUrl || '', requiredLevel: d.data().requiredLevel || 1, isActive: d.data().isActive !== false }))
          .filter(m => itemMaps.includes(m.name))
          .sort((a, b) => {
            const ia = customMapOrder.indexOf(a.name);
            const ib = customMapOrder.indexOf(b.name);
            if (ia !== -1 && ib !== -1) return ia - ib;
            if (ia !== -1) return -1;
            if (ib !== -1) return 1;
            return a.name.localeCompare(b.name, 'th');
          });

        if (props.mapName) {
          const found = allowedMaps.value.find(m => m.name === props.mapName);
          selectedMap.value = found ? found.id : '';
        }
      } catch (e) { error.value = 'ไม่สามารถโหลดแผนที่: ' + e.message; }
    };

    // FIX #9 – store unsubscribe reference
    const subscribeWaitingLoot = () => {
      if (!user.value) return;
      unsubLootEvents = projectFirestore.collection('events')
        .where('type', '==', 'waiting_loot')
        .where('ownerUids', 'array-contains', user.value.uid)
        .where('finished', '==', false)
        .onSnapshot(snap => {
          snap.docChanges().forEach(change => {
            if (change.type === 'added') {
              const d = change.doc.data();
              emit('start-battle', { eventId: change.doc.id, opponentUid: d.characterId === user.value.uid ? d.victimUid : d.characterId, mapName: d.map });
            }
          });
        });
    };

    const hints = ['ซุ่มหลังต้นไม้...', 'แอบดูจากพุ่มไม้...', 'คืบคลานแบบเสือดำ...', 'เงียบไว้… ซอมบี้เดินมา', 'เล็งตำแหน่งแล้ว!'];
    let hintTimer = null;
    const cycleHints = () => {
      clearInterval(hintTimer);
      let i = 0;
      hintText.value = hints[0];
      hintTimer = setInterval(() => { i++; hintText.value = hints[i % hints.length]; }, 600);
    };

    const startLoot = async () => {
      if (!selectedMap.value || isLooting.value) return;
      isLooting.value = true;
      moveToRight.value = false;
      cycleHints();
      await new Promise(r => setTimeout(r, 2000));
      try {
        const fn = projectFunctions.httpsCallable('gameActions');
        const selectedMapData = allowedMaps.value.find(m => m.id === selectedMap.value);
        const instanceId = props.item.instanceId || props.item.id;
        await fn({ action: 'ambushActions', subAction: 'useLoot', instanceId, mapName: selectedMapData?.name || '' });
        moveToRight.value = true;
        await new Promise(r => setTimeout(r, 800));
        emit('loot-success', instanceId);
      } catch (e) {
        error.value = e?.message || 'เกิดข้อผิดพลาดในการดักรอปล้น';
      } finally {
        clearInterval(hintTimer);
        isLooting.value = false;
        moveToRight.value = false;
      }
    };

    onMounted(async () => {
      await fetchMaps();
      subscribeWaitingLoot();
      isLoading.value = false;
    });

    // FIX #9 – cleanup on destroy
    onUnmounted(() => {
      clearInterval(hintTimer);
      if (unsubLootEvents) unsubLootEvents();
    });

    return {
      isLoading, error, isAdmin, isLooting, hintText, moveToRight,
      allowedMaps, selectedMap, defaultItemImg, startLoot, handleImageError
    };
  }
};
</script>

<style scoped>
/* ── Scrollbar ── */
.scrollbar-thin::-webkit-scrollbar { width: 4px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #1f2937; border-radius: 9999px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #374151; border-radius: 9999px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #4b5563; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #374151 #1f2937; }

/* ── Custom utilities (replaces TW2-incompatible classes) ── */
.text-xxs { font-size: 10px; line-height: 1.2; }

/* max-h arbitrary → custom class */
.loot-body-scroll { max-height: 72vh; }

/* bg/border opacity replacements */
.bg-red-900-20    { background-color: rgba(127, 29, 29, 0.20); }
.bg-yellow-900-20 { background-color: rgba(120, 53, 15, 0.20); }
.border-yellow-800-50 { border-color: rgba(92, 45, 11, 0.50); }

/* map card selected (replaces bg-indigo-900/20 + ring) */
.map-selected-bg {
  background-color: rgba(49, 46, 129, 0.20);
  box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.30);
}

/* ── Scout animation ── */
.scout-path-line {
  top: 50%;
  margin-top: -1px;
}
.scout-runner {
  top: 50%;
  transform: translateY(-50%);
  animation: scoutLoop 2s ease-in-out infinite;
  width: calc(100% - 16px);
}
.scout-runner.scout-go {
  animation: scoutRight 0.9s ease-out forwards;
}
.scout-bush { opacity: 0.6; }
.scout-bush-center {
  left: 50%;
  transform: translateX(-50%);
}
@keyframes scoutLoop {
  0%   { transform: translateY(-50%) translateX(0); }
  50%  { transform: translateY(-50%) translateX(100%); }
  100% { transform: translateY(-50%) translateX(0); }
}
@keyframes scoutRight {
  to { transform: translateY(-50%) translateX(100%); }
}
@keyframes wiggle { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(-3deg); } }
.animate-wiggle      { animation: wiggle 1.2s ease-in-out infinite; }
.animate-wiggle-slow { animation: wiggle 1.7s ease-in-out infinite; }

/* ── Animations ── */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

button:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
